import { MB_Modal } from '@/components/general/modal';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import React from 'react';
import { StyleProp, StyleSheet, Text, View, ViewStyle, TextStyle } from 'react-native';
import { AnimatedCircularProgress, AnimatedCircularProgressProps } from 'react-native-circular-progress';

interface MB_ModalProgressCircleProps {
  /**
   * Modal visibility.
   */
  isVisible: boolean;

  /**
   * Current progress.
   */
  progress: number;

  /**
   * The style of the progress circle's parent container.
   */
  containerStyle?: StyleProp<ViewStyle>;

  /**
   * The text below the progress circle.
   */
  bottomText?: string;

  /**
   * The props to pass to the AnimatedCircularProgress component.
   *
   * See https://github.com/bartgryszko/react-native-circular-progress#configuration
   */
  circularProgressProps?: Partial<AnimatedCircularProgressProps>;

  /**
   * The style of the percentage completed container.
   */
  percentageCompletedContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the percentage text.
   */
  percentageTextStyle?: StyleProp<TextStyle>;

  /**
   * The style of the completed text.
   */
  completedTextStyle?: StyleProp<TextStyle>;
}

interface MB_ModalProgressCircleBodyProps {
  /**
   * Current progress.
   */
  progress: number;

  /**
   * The props to pass to the AnimatedCircularProgress component.
   *
   * See https://github.com/bartgryszko/react-native-circular-progress#configuration
   */
  circularProgressProps?: Partial<AnimatedCircularProgressProps>;

  /**
   * The style of the percentage completed container.
   */
  percentageCompletedContainerStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the percentage text.
   */
  percentageTextStyle?: StyleProp<TextStyle>;

  /**
   * The style of the completed text.
   */
  completedTextStyle?: StyleProp<TextStyle>;

  /**
   * Custom children to render inside the progress circle.
   */
  children?: (fill: number) => React.JSX.Element;
}

const MB_ModalProgressCircleBody = ({
  progress,
  circularProgressProps,
  percentageCompletedContainerStyle,
  percentageTextStyle,
  completedTextStyle,
  children,
}: MB_ModalProgressCircleBodyProps) => {
  return (
    <AnimatedCircularProgress
      size={140}
      width={3}
      backgroundWidth={16}
      fill={progress}
      tintColor={'white'}
      backgroundColor={mbApplyTransparency('#9A4C95', 0.6)}
      {...circularProgressProps}
    >
      {fill => {
        if (children) {
          return children(fill);
        }

        return (
          <View style={percentageCompletedContainerStyle ?? { alignItems: 'center', justifyContent: 'center' }}>
            <Text style={[styles.percentageText, percentageTextStyle]}>{Math.round(fill)}%</Text>
            <Text style={[styles.completedText, completedTextStyle]}>Completed</Text>
          </View>
        );
      }}
    </AnimatedCircularProgress>
  );
};

const MB_ModalProgressCircle = ({
  isVisible,
  progress,
  containerStyle,
  bottomText,
  circularProgressProps,
  percentageCompletedContainerStyle,
  percentageTextStyle,
  completedTextStyle,
}: MB_ModalProgressCircleProps) => {
  return (
    <MB_Modal isVisible={isVisible} hideCloseButton simplified>
      <View style={[styles.container, containerStyle]}>
        <MB_ModalProgressCircleBody
          progress={progress}
          circularProgressProps={circularProgressProps}
          percentageCompletedContainerStyle={percentageCompletedContainerStyle}
          percentageTextStyle={percentageTextStyle}
          completedTextStyle={completedTextStyle}
        />
        <Text style={styles.bottomText}>{bottomText}</Text>
      </View>
    </MB_Modal>
  );
};

export { MB_ModalProgressCircle, MB_ModalProgressCircleBody };
export type { MB_ModalProgressCircleBodyProps, MB_ModalProgressCircleProps };

const baseStyles = StyleSheet.create({
  smallerText: {
    fontWeight: 'normal',
    fontSize: 12,
    color: '#ffffff',
    textAlign: 'center',
  },
  largerText: {
    // TODO: Add font
    fontSize: 28,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
});

const styles = StyleSheet.create({
  container: {
    borderRadius: 10,
    paddingHorizontal: 50,
    paddingVertical: 32,
    backgroundColor: '#1D1A31',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomText: StyleSheet.flatten([
    baseStyles.smallerText,
    {
      marginTop: 18,
      color: '#BAC0CA',
    },
  ]),

  completedText: StyleSheet.flatten([baseStyles.smallerText]),

  percentageText: StyleSheet.flatten([
    baseStyles.largerText,
    {
      marginHorizontal: 6,
      color: '#BAC0CA',
      fontSize: 25,
    },
  ]),
});
